import React from 'react'
import {
  CBadge,
  CCard,
  CCardBody,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormSwitch,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { InfoBox } from 'src/components/common/ImportWizard'

const validationSchema = Yup.object().shape({
  captain_cards: Yup.number()
    .typeError('Enter a number')
    .required('Use limit is required')
    .integer('Use limit must be a whole number')
    .min(1, 'Use limit must be at least 1'),
})

const TripleCap = (props) => {
  const formik = useFormik({
    initialValues: {
      captain_cards: props.data?.captain_cards ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      data.type = 'captain_cards'
      props.setTripleCapLoader(true)
      CommonService.editPowerControl(data)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            // The endpoint answers with the pre-save row, so keep the saved value locally
            // to stay in step with the "Use Limit" stat card above.
            props.setData((previous) => ({
              ...(previous || {}),
              captain_cards: data.captain_cards,
            }))
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Could not save the use limit.', 'error')
        })
        .finally(() => props.setTripleCapLoader(false))
    },
  })

  const handleTripleCheck = (event) => {
    const checked = event.target.checked
    props.setTripleCheck(checked)
    CommonService.changePowerControlStatus({ status: checked, type: 'captain_cards_status' })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
        } else {
          props.setTripleCheck(!checked)
          ToastComponent(res.message, 'error')
        }
      })
      .catch((e) => {
        // Put the switch back where it was - the saved status did not change.
        props.setTripleCheck(!checked)
        ToastComponent(e.response?.data?.message || 'Could not change the status.', 'error')
      })
  }

  const limitError = formik.touched.captain_cards ? formik.errors.captain_cards : null

  return (
    <CCard className="pc-panel">
      <CCardBody>
        <div className="pc-panel-toolbar">
          <div>
            <h2 className="pc-panel-title">Triple Captain</h2>
            <p className="pc-panel-note">
              Members play this power to triple their captain&apos;s points for a round.
            </p>
          </div>
          <CBadge
            color={props.tripleCheck ? 'success' : 'secondary'}
            shape="rounded-pill"
            className="px-3 py-2"
          >
            {props.tripleCheck ? 'Active' : 'Inactive'}
          </CBadge>
        </div>

        <InfoBox color="info">
          While the power is off, members can&apos;t play it in the app. Changing the use limit
          applies from the next time a member plays it - powers already used stay on record.
        </InfoBox>

        {props.loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading power settings...</span>
          </div>
        ) : (
          <CForm onSubmit={formik.handleSubmit}>
            <div className="pc-setting">
              <div>
                <p className="pc-setting-label">Status</p>
                <p className="pc-setting-help">
                  Show the Triple Captain power to members. Saved as soon as you switch it.
                </p>
              </div>
              <div className="pc-setting-control">
                <CFormSwitch
                  label={props.tripleCheck ? 'On' : 'Off'}
                  id="triple_captain"
                  name="captain_cards_check"
                  checked={!!props.tripleCheck}
                  onChange={handleTripleCheck}
                />
              </div>
            </div>

            <div className="pc-setting">
              <div>
                <p className="pc-setting-label">Use Limit</p>
                <p className="pc-setting-help">How many times a member can play Triple Captain.</p>
              </div>
              <div className="pc-setting-control">
                <CFormInput
                  type="number"
                  min={1}
                  id="captain_cards"
                  name="captain_cards"
                  className="pc-limit-input"
                  value={formik.values.captain_cards}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!limitError}
                  aria-describedby="captain_cards_feedback"
                />
                {limitError && (
                  <CFormFeedback id="captain_cards_feedback" invalid className="d-block">
                    {limitError}
                  </CFormFeedback>
                )}
              </div>
            </div>

            <div className="pc-form-actions">
              <CLoadingButton
                type="submit"
                color="primary"
                loading={props.tripleCapLoader}
                disabled={!formik.dirty}
              >
                Save Changes
              </CLoadingButton>
              {formik.dirty && (
                <CBadge color="warning" shape="rounded-pill" className="px-3 py-2">
                  Unsaved changes
                </CBadge>
              )}
            </div>
          </CForm>
        )}
      </CCardBody>
    </CCard>
  )
}

export default TripleCap
